import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import { withStyles } from '@material-ui/styles';

import { compose } from 'redux';
import withScrollListener from "../../../../../hocs/with_scroll_listener.js";
import withShowModal from '../../../../../hocs/with_show_modal.js';

import usePrevious from "../../../../../hooks/use_previous.js";

import NavigationTabContent from "../../../components/navigation_tab_content.js";

import TableView  from "../../../../../base/table/table_view.js";
import FormFields from "../../../../../base/form/form_fields.js";

import { ClearItemValue } from "../../../../../base/inputs/constants.js";

import pageStyles from "../../../../styles/page_styles.js.jsx";
import { infoPageStyles } from "../../../base_info_page.js.jsx";

import { EditParticipantType } from "../../../../modals/constants.js";

import { ProjectInvestmentModel } from "../../../../../models/projects/project_investment.js";
import ListInvestmentsUseCase from "../../../../../use_cases/admin/projects/investments/list_investments_use_case.js";

const styles = theme => ({
  root: {
    position: "relative"
  },
  filters: {
    marginBottom: "15px"
  }
});


const ProjectInvestmentsTab = forwardRef((props, ref) => {
  
  const pageSize = 50;

  const { 
    classes, project = {},
    onScrollToEndProcessed, onDataLoaded, onChangeFilter } = props;

  const [investments, setInvestments] = useState([]);
  const [onceLoaded, setOnceLoaded] = useState(false);
  const [filterData, setFilterData] = useState(props.filterData);

  const previousInvestments = usePrevious(investments);  

  const internalRef = useRef();

  useEffect(() => {
    internalRef.current = {
      paginationInfo:  {
        page: 1,
        perPage: pageSize,
        hasMore: true
      }
    }
  }, []);

  useEffect(() => {
    if(onceLoaded) {
      return;
    }
    if(!props.visible) {
      return;
    }
    loadInvestments();
  }, [props.visible]);

  useEffect(() => {
    const { current : { paginationInfo } } = internalRef;

    let prevCount = (previousInvestments || []).length;
    let currCount = investments.length;
    if(prevCount < currCount) {
      onDataLoaded(paginationInfo.hasMore);
    }
  }, [investments]);

  useEffect(() => {
    internalRef.current = {
      paginationInfo:  {
        page: 1,
        perPage: pageSize,
        hasMore: true
      }
    }
    loadInvestments({forceReload: true});
  }, [filterData]);

  const loadInvestments = ({forceReload = false} = {}) => {
    if(!props.visible) {
      return;
    }
    
    const { current : { paginationInfo } } = internalRef;

    const params = {
      ...paginationInfo,
      ...filterData,
      projectId: project.id
    }

    if(ListInvestmentsUseCase.cancel) {
      ListInvestmentsUseCase.cancel();
    }

    return ListInvestmentsUseCase.execute(params)
      .then((result) => {
        const { data: { list, paginationInfo : newPaginationInfo } } = result;

        const processedList = list.map((investment) => {
          return investment;
        })
        
        let newList = investments.concat(processedList);
        if(forceReload) {
          newList = processedList;
        }

        setOnceLoaded(true);
        internalRef.current.paginationInfo = newPaginationInfo;
        onScrollToEndProcessed(paginationInfo.hasMore);

        setInvestments(newList);
      })
      .catch((result) => {

      });
  }

  useImperativeHandle(ref, () => ({

    onScrolledToEnd() {
      const { current : { paginationInfo } } = internalRef;
      if(!paginationInfo.hasMore) {
        return;
      }
      const { page } = paginationInfo;

      let newPaginationInfo = {
        ...paginationInfo,
        page: page + 1,
      }; 
      internalRef.current.paginationInfo = newPaginationInfo

      loadInvestments();
    }
  }));

  const onChangeFilterField = (newItem, field, newValue) => {
    let value = newValue;
    let fieldName = field.name;
    if(field.type === "enum" && value === ClearItemValue) {
      value = undefined;
    }
    if(field.type === "model") {
      fieldName = field.foreignKey;
      value = newItem[fieldName];
    }
    const newFilterData = {
      ...filterData,
      [fieldName]: value
    };
    setFilterData(newFilterData);
    onChangeFilter(fieldName, value);
    
  }

  const onInvestmentUpdated = (investment) => {
    investment.key = investment.updatedAt;
    let list = [...investments];

    const index = list.findIndex(element => element.id === investment.id);

    if(index < 0) {
      return;
    }
    list[index] = investment;
    setInvestments(list);
  }

  const onClickRow = investment => {
    const { showModal } = props;
    showModal(EditParticipantType, {
      investment,
      onSave: onInvestmentUpdated
    })
  }

  const columnsWhiteList = ["user", "contract", "paymentMethod", "status", "activityStatus", "amount", "bonuscode", "taxId", "calculatedInterest", "createdAt", "isUserIdentified"];
  const columns = ProjectInvestmentModel.listFields().filter(c => columnsWhiteList.includes(c.name));

  return (
    <NavigationTabContent visible = { props.visible } >
      <div className = { classes.root } >
        <div className = { classes.filters } >
          <FormFields formFields = { ProjectInvestmentModel.listFilters() } 
                      item = { filterData }
                      onItemChange = { onChangeFilterField } 
                      defaultWeight = { 3 } />
        </div>
        <div className = { classes.list } >
          <TableView columns = { columns }
                     items = { investments } 
                     onClickRow = { onClickRow } />
        </div>
      </div>
    </NavigationTabContent>
  );
});

ProjectInvestmentsTab.defaultProps = {
}

export default compose(
  withShowModal,
  withScrollListener,
  withStyles(
    (theme) => ({
      ...pageStyles(theme),
      ...infoPageStyles(theme),
      ...styles(theme),
    }),
    { withTheme: true }
  )
)(ProjectInvestmentsTab);
